function num(value, fallback = 0) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value))
}

function average(candles, index, length, field) {
    if (!candles || index < 0 || length <= 0) return null
    const start = Math.max(0, index - length + 1)
    let total = 0
    let count = 0

    for (let i = start; i <= index; i++) {
        const value = num(candles[i] && candles[i][field], NaN)
        if (!Number.isFinite(value)) continue
        total += value
        count++
    }

    return count > 0 ? total / count : null
}

function averageRange(candles, index, length) {
    if (!candles || index < 0 || length <= 0) return null
    const start = Math.max(0, index - length + 1)
    let total = 0
    let count = 0

    for (let i = start; i <= index; i++) {
        const candle = candles[i]
        if (!candle) continue
        const high = num(candle[2], NaN)
        const low = num(candle[3], NaN)
        if (!Number.isFinite(high) || !Number.isFinite(low)) continue
        total += Math.abs(high - low)
        count++
    }

    return count > 0 ? total / count : null
}

function donchian(candles, index, length) {
    let high = 0
    let low = Number.MAX_VALUE

    for (let i = 0; i < length; i++) {
        const candle = candles[index - i]
        if (!candle) continue
        high = Math.max(high, num(candle[2], high))
        low = Math.min(low, num(candle[3], low))
    }

    if (low === Number.MAX_VALUE) low = 0
    return { high, low, mid: low + Math.abs((high - low) / 2) }
}

function sma(candles, index, length) {
    if (index < length) return ''
    let total = 0

    for (let i = 0; i < length; i++) {
        const candle = candles[index - i] || candles[0]
        total += num(candle && candle[4])
    }

    return total / length
}

function candleBodyRatio(candle) {
    if (!candle) return 1
    const range = Math.abs(num(candle[2]) - num(candle[3]))
    if (range <= 0) return 1
    return Math.abs(num(candle[4]) - num(candle[1])) / range
}

function scoreVolume(candles, index, lookback) {
    const candle = candles[index]
    const volume = num(candle && candle[5])
    const avgVolume = average(candles, index - 1, lookback, 5)

    if (!volume || !avgVolume) return 7
    if (volume >= avgVolume * 1.5) return 15
    if (volume >= avgVolume * 1.15) return 11
    if (volume >= avgVolume) return 8
    return 4
}

export function calculateVTConfidence(candles, index, direction, signal, options = {}) {
    if (!candles || !candles[index] || !signal) return 0

    const trendLookback = options.trendLookback || 20
    const volumeLookback = options.volumeLookback || 20
    const rangeLookback = options.rangeLookback || 14
    const candle = candles[index]
    const close = num(candle[4])
    const open = num(candle[1])
    const high = num(candle[2])
    const low = num(candle[3])
    const body = Math.abs(close - open)
    const range = Math.abs(high - low)
    const isBuy = direction === 'BUY'

    let score = 0

    const currentSma = Number.isFinite(signal.sma) ? signal.sma : null
    const prevSma = index > 0 ? average(candles, index - 1, options.smaLength || 50, 4) : null
    if (currentSma !== null && prevSma !== null) {
        const slope = currentSma - prevSma
        if ((isBuy && slope > 0) || (!isBuy && slope < 0)) score += 10
        if ((isBuy && close >= currentSma) || (!isBuy && close <= currentSma)) score += 8
    } else {
        score += 8
    }

    const trendBase = candles[index - trendLookback]
    if (trendBase) {
        const recentMove = close - num(trendBase[4])
        if ((isBuy && recentMove > 0) || (!isBuy && recentMove < 0)) score += 7
    } else {
        score += 3
    }

    const entryPoint = num(signal.entryPoint)
    const stoploss = num(signal.entryStoploss)
    const target = num(signal.entryTarget)
    if ((isBuy && close >= entryPoint) || (!isBuy && close <= entryPoint)) {
        score += 8
    } else if ((isBuy && high >= entryPoint) || (!isBuy && low <= entryPoint)) {
        score += 4
    }

    if ((isBuy && close >= open) || (!isBuy && close <= open)) score += 6
    if (range > 0) {
        const bodyStrength = body / range
        if (bodyStrength >= 0.6) score += 6
        else if (bodyStrength >= 0.35) score += 3
    }

    const risk = Math.abs(entryPoint - stoploss)
    const avgRange = averageRange(candles, index - 1, rangeLookback)
    if (risk > 0) score += 5
    if (risk > 0 && avgRange) {
        if (risk <= avgRange * 1.5) score += 7
        else if (risk <= avgRange * 2.5) score += 4
        else score += 1
    } else {
        score += 4
    }

    const setupCandle = candles[signal.setupEndIndex] || candles[signal.setupStartIndex]
    const setupRatio = candleBodyRatio(setupCandle)
    if (setupRatio <= 0.3) score += 8
    else if (setupRatio <= 0.5) score += 5
    else score += 2

    score += scoreVolume(candles, index, volumeLookback)

    if (signal.dcHigh && signal.dcLow) {
        if ((isBuy && close >= signal.dcMid) || (!isBuy && close <= signal.dcMid)) score += 6
        if ((isBuy && high >= signal.dcHigh) || (!isBuy && low <= signal.dcLow)) score += 8
    } else {
        score += 6
    }

    if (risk > 0 && target) {
        const remaining = isBuy ? target - close : close - target
        if (remaining >= risk) score += 6
        else if (remaining >= risk * 0.5) score += 3
    }

    return Math.round(clamp(score, options.minScore || 20, options.maxScore || 100))
}

export function extractVTConfidenceSignals(candles, options = {}) {
    if (!Array.isArray(candles) || candles.length === 0) return []

    const riskRewardRatio = options.riskRewardRatio || 3
    const smaLen = options.smaLength || 50
    const dcLen = options.dcLength || 20
    const signals = []

    let prev
    let trendChanged = false
    let starFormed = false
    let blackCandleFormed = false
    let entryDirection = ''
    let entryPoint = 0
    let entrySL = 0
    let entryTarget = 0
    let setupStartIndex = null
    let setupEndIndex = null

    for (let i = 0; i < candles.length; i++) {
        const p = candles[i]
        if (!p || p.length < 5) continue

        const now = {
            index: i,
            date: p[0],
            open: num(p[1]),
            high: num(p[2]),
            low: num(p[3]),
            close: num(p[4])
        }

        now.sma = sma(candles, i, smaLen)
        const dc = donchian(candles, i, dcLen)
        now.dcHigh = dc.high
        now.dcLow = dc.low
        now.dcMid = dc.mid

        const candleSize = Math.abs(now.high - now.low)
        const bodySize = Math.abs(now.open - now.close)
        now.blackCandle = candleSize > 0 && bodySize <= candleSize / 2
        now.kuchiSize = now.blackCandle ? candleSize - bodySize : 0

        if (!prev) {
            prev = now
            prev.smaColor = ''
        }

        now.smaColor = now.sma > prev.sma ? 'GREEN' : 'RED'

        if (now.smaColor === prev.smaColor) {
            now.trendChanged = false
        } else {
            trendChanged = true
            now.trendChanged = trendChanged
            starFormed = false
            blackCandleFormed = false
            entryDirection = ''
            entryPoint = 0
            entrySL = 0
            entryTarget = 0
            setupStartIndex = null
            setupEndIndex = null
        }

        if (now.trendChanged !== prev.trendChanged) {
            starFormed = false
        }

        if (prev.blackCandle && now.trendChanged) {
            starFormed = true
            blackCandleFormed = true
            setupStartIndex = prev.index
            setupEndIndex = prev.index

            if (now.smaColor === 'GREEN') {
                entryDirection = 'BUY'
                entryPoint = prev.high
                entrySL = prev.low
                entryTarget = entryPoint + Number((entryPoint - entrySL) * riskRewardRatio)
            } else {
                entryDirection = 'SELL'
                entryPoint = prev.low
                entrySL = prev.high
                entryTarget = entryPoint - Number((entrySL - entryPoint) * riskRewardRatio)
            }
        } else {
            starFormed = false
        }

        if ((starFormed || trendChanged) && now.blackCandle) {
            blackCandleFormed = true
            if (setupStartIndex === null) setupStartIndex = now.index
            setupEndIndex = now.index

            if (now.smaColor === 'GREEN') {
                entryDirection = 'BUY'
                if (entryPoint === 0) entryPoint = now.high
                if (entrySL === 0) entrySL = now.low
                if (now.low < entrySL) entrySL = now.low
                if (now.high > entryPoint) entryPoint = now.high
                entryTarget = entryPoint + Number((entryPoint - entrySL) * riskRewardRatio)
            } else {
                entryDirection = 'SELL'
                if (entryPoint === 0) entryPoint = now.low
                if (entrySL === 0) entrySL = now.high
                if (now.high > entrySL) entrySL = now.high
                if (now.low < entryPoint) entryPoint = now.low
                entryTarget = entryPoint - Number((entrySL - entryPoint) * riskRewardRatio)
            }
        }

        if ((starFormed || trendChanged) && blackCandleFormed && !now.blackCandle) {
            trendChanged = false
            now.isEntry = true
        }

        if (now.isEntry && entryDirection) {
            const signal = {
                ...now,
                entryDirection,
                entryPoint: Number(entryPoint),
                entryStoploss: Number(entrySL),
                entryTarget: Number(entryTarget),
                setupStartIndex,
                setupEndIndex
            }

            signal.confidence = calculateVTConfidence(
                candles,
                i,
                entryDirection,
                signal,
                { ...options, smaLength: smaLen }
            )
            signals.push(signal)

            trendChanged = false
            blackCandleFormed = false
            starFormed = false
            entryDirection = ''
            entrySL = 0
            entryPoint = 0
            entryTarget = 0
            setupStartIndex = null
            setupEndIndex = null
        }

        prev = now
    }

    return signals
}
